import type { Broker } from '~~/types/Broker'
import type { Database } from '~~/types/supabase'

export type BrokerFromSupabase = Database['public']['Views']['broker_summary']['Row']

export const useBroker = () => {
  const supabase = useSupabaseClient<Database>()
  const broker = useState<Broker | null>('broker', () => null)

  const getSalesFrequency = (totalHousesSold: number): string => {
    return totalHousesSold > 100 ? 'actieve verkoper' : 'gerichte verkoper'
  }

  const getPriceComparison = (medianPrice: number): string => {
    const cityMedian = 300000 // Example value; should be calculated or fetched
    return medianPrice > cityMedian ? 'hogere prijsklasse' : 'concurrerende prijsklasse'
  }

  const getPriceSegment = (medianPrice: number): string => {
    if (medianPrice < 200000) return 'betaalbaar segment'
    if (medianPrice >= 200000 && medianPrice < 400000) return 'midden segment'
    return 'hogere segment'
  }

  const compareSalesToRegion = (totalHousesSold: number): string => {
    const averageHousesSoldInRegion = 100
    if (totalHousesSold > averageHousesSoldInRegion * 1.1) {
      return 'meer woningen'
    }
    else if (totalHousesSold < averageHousesSoldInRegion * 0.9) {
      return 'ongeveer evenveel woningen' // eigenlijk minder woningen
    }
    else {
      return 'ongeveer evenveel woningen'
    }
  }

  const cleanBroker = (broker: BrokerFromSupabase): Broker => ({
    broker_name: broker.broker_name ?? '',
    broker_slug: broker.broker_slug ?? '',
    url: `/makelaar/${broker.broker_slug}/`,
    markdown_url: `[${broker.broker_name}]( /makelaar/${broker.broker_slug}/ )`,
    html_url: `<a href="/makelaar/${broker.broker_slug}/">${broker.broker_name}</a>`,
    html_location_url: `<a href="/makelaars/${broker.province_slug}/${broker.city_slug}/">${broker.city_name}</a>`,
    location_url: `/makelaars/${broker.province_slug}/${broker.city_slug}/`,
    city_name: broker.city_name ?? '',
    city_slug: broker.city_slug ?? '',
    province_name: broker.province_name ?? '',
    province_slug: broker.province_slug ?? '',

    total_houses_sold: broker.total_houses_sold ?? 0,
    avg_house_price: euro(broker.avg_house_price ?? 0),
    median_price: euro(broker.median_price ?? 0),
    houses_sold_last_year: broker.houses_sold_last_year ?? 0,
    houses_sold_last_30_days: broker.houses_sold_last_30_days ?? 0,
    houses_sold_last_3_months: broker.houses_sold_last_3_months ?? 0,
    houses_sold_last_6_months: broker.houses_sold_last_6_months ?? 0,
    houses_sold_last_12_months: broker.houses_sold_last_12_months ?? 0,
    avg_days_on_market: broker.avg_days_on_market ?? 0,
    broker_cost_min: euro(broker.broker_cost_min ?? 0),
    broker_cost_max: euro(broker.broker_cost_max ?? 0),
    buying_agent_min: euro(broker.buying_agent_min ?? 0),
    buying_agent_max: euro(broker.buying_agent_max ?? 0),

    sales_frequency: getSalesFrequency(broker.total_houses_sold ?? 0),
    price_comparison: getPriceComparison(broker.median_price ?? 0),
    price_segment: getPriceSegment(broker.median_price ?? 0),
    sales_comparison: compareSalesToRegion(broker.total_houses_sold ?? 0),
    top_5_locations: (broker?.top_5_locations ?? []).map(item => ({
      id: item?.id ?? '',
      name: item?.city_name ?? '',
      avg_price: euro(item?.avg_price ?? 0),
      transactions_count: item?.transactions_count ?? 0,
      city_slug: item?.city_slug ?? '',
      province_slug: item?.province_slug ?? '',
    })),
    property_types_sold: (broker?.property_types_sold ?? []).map(item => ({
      type_woning: item?.type_woning ?? '',
      count: item?.count ?? 0,
    })),
    similar_brokers: (broker?.similar_brokers ?? []).map(item => ({
      broker_name: item?.broker_name ?? '',
      broker_slug: item?.broker_slug ?? '',
      url: `/makelaar/${item?.broker_slug}`,
    })),
  })

  const fetchBroker = async (slug: string) => {
    const { data, error } = await supabase.from('broker_summary').select('*').eq('broker_slug', slug).single()
    if (error) throw error
    const content = cleanBroker(data)
    broker.value = content
    return content
  }

  return {
    // state
    broker,

    // methods
    fetchBroker,
  }
}
